import type { GUID } from "../types/GUID"

/**
 * Generates a cryptographically random 128-bit UUID with timestamp prefix.
 * Uses crypto.getRandomValues for randomness and embeds current timestamp in first 6 bytes.
 * Returns a 256-bit GUID (stored as bigint) with version 7 and variant 2 markers.
 *
 * @returns GUID (256-bit bigint) with embedded timestamp and random data
 *
 * @example
 * ```ts
 * import { UUID } from "@pdfking/tfunc"
 *
 * const id1 = UUID()
 * const id2 = UUID()
 * console.log(id1 !== id2) // true (always unique)
 *
 * const str = GuidToString(id1)
 * console.log(str) // Base-36 string representation
 * ```
 */
export const UUID = (): GUID => {
  const value = new Uint8Array(16)
  crypto.getRandomValues(value)

  const timestamp = BigInt(Date.now())
  value[0] = Number((timestamp >> 40n) & 0xffn)
  value[1] = Number((timestamp >> 32n) & 0xffn)
  value[2] = Number((timestamp >> 24n) & 0xffn)
  value[3] = Number((timestamp >> 16n) & 0xffn)
  value[4] = Number((timestamp >> 8n) & 0xffn)
  value[5] = Number(timestamp & 0xffn)

  value[6] = (value[6] & 0x0f) | 0x70
  value[8] = (value[8] & 0x3f) | 0x80

  // Convert 16 bytes into a single BigInt
  let result = 0n
  for (const byte of value) {
    result = (result << 8n) | BigInt(byte)
  }
  return result as GUID
}
